/**
 * Client-side image compression utility using HTML5 Canvas.
 *
 * This function reads an input image File and optional dimension/quality settings,
 * and writes an optimized, compressed JPEG File blob.
 */

export interface CompressImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
  timeoutMs?: number;
}

/**
 * Resizes and compresses an image file to reduce file size for fast mobile upload
 * and to respect Supabase storage limits (e.g. 5MB cap).
 */
export async function compressImage(
  file: File,
  options: CompressImageOptions = {},
): Promise<File> {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.82,
    mimeType = "image/jpeg",
    timeoutMs = 4000,
  } = options;

  // Non-image files or SVG/GIF files where canvas redraw may break animation/vector
  if (
    !file.type.startsWith("image/") ||
    file.type === "image/svg+xml" ||
    file.type === "image/gif"
  ) {
    return file;
  }

  // If the file is already small (e.g. < 400 KB) and already JPEG/PNG/WebP, return as is
  if (
    file.size < 400 * 1024 &&
    (file.type === "image/jpeg" || file.type === "image/webp")
  ) {
    return file;
  }

  // In non-browser environments or if canvas / Image / FileReader is unavailable, fallback gracefully
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof FileReader === "undefined"
  ) {
    return file;
  }

  return new Promise((resolve) => {
    let finished = false;
    const finish = (result: File) => {
      if (!finished) {
        finished = true;
        clearTimeout(timer);
        resolve(result);
      }
    };

    const timer = setTimeout(() => {
      finish(file);
    }, timeoutMs);

    const reader = new FileReader();

    reader.onerror = () => finish(file);

    reader.onload = (event) => {
      try {
        const img = new Image();

        img.onerror = () => finish(file);

        img.onload = () => {
          try {
            let { width, height } = img;

            // Downscale if either dimension exceeds bounds while preserving aspect ratio
            if (width > maxWidth || height > maxHeight) {
              if (width / height > maxWidth / maxHeight) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
              } else {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
              }
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              finish(file);
              return;
            }

            // Fill white background in case of transparent PNG converted to JPEG
            if (mimeType === "image/jpeg") {
              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, width, height);
            }

            ctx.drawImage(img, 0, 0, width, height);

            if (typeof canvas.toBlob !== "function") {
              finish(file);
              return;
            }

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  finish(file);
                  return;
                }

                // Replace file extension with .jpg if converting to JPEG
                const baseName =
                  file.name.substring(0, file.name.lastIndexOf(".")) ||
                  file.name;
                const extension = mimeType === "image/jpeg" ? "jpg" : "webp";
                const newFileName = `${baseName}.${extension}`;

                const compressedFile = new File([blob], newFileName, {
                  type: mimeType,
                  lastModified: Date.now(),
                });

                // Return whichever is smaller, or the compressed file if original was too large
                if (
                  compressedFile.size < file.size ||
                  file.size > 5 * 1024 * 1024
                ) {
                  finish(compressedFile);
                } else {
                  finish(file);
                }
              },
              mimeType,
              quality,
            );
          } catch {
            finish(file);
          }
        };

        img.src = event.target?.result as string;
      } catch {
        finish(file);
      }
    };

    reader.readAsDataURL(file);
  });
}
